import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import './ReportSourcesView.css'; // Reuse existing CSS
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useLocation } from 'react-router-dom';
import Loader from './components/Loader';
import PageInfo from './components/PageInfo';
import env from './utils/env';

// Define the interface for the SearchResult data structure based on MongoDB data
export interface SearchResult {
    _id: string;
    url: string;
    index: number;
    query: string;
    update_timestamp: string | null;
    target_id: string[]; // Now always an array of string IDs
    checked_on_pdf_timestamp: string | null;
    pdf_urls: string[];
    processed: boolean | number;
    insert_timestamp: string | null;
    cusip: string | null;
    has_isin: boolean;
    isin: string | null;
    validated: boolean;
    prepare_pdf_urls: string[];
    checking_failed: boolean;
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface ApiResponse {
    searchResults: SearchResult[];
    pagination?: PaginationMetadata;
}

// Add a helper function to show count of target IDs
const formatTargetIdDisplay = (targetIds: string[]): string => {
    if (!targetIds || targetIds.length === 0) {
        return 'None';
    } else if (targetIds.length === 1) {
        return '1';
    } else {
        return `${targetIds.length}`;
    }
};

const SearchResultsView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const limit = 50; // Number of items per page
    
    // State for details popup
    const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);

    // Get target_id from query parameters
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const targetIdRaw = queryParams.get('target_id');

    // Parse the target_id parameter into an array, even if it's a single value
    const parseTargetIds = (): string[] => {
        if (!targetIdRaw) return [];
        // Split by comma to handle multiple IDs
        return targetIdRaw.split(',').map(id => id.trim()).filter(id => id);
    };

    // Store parsed target IDs
    const targetIds = parseTargetIds();

    // Fetch search results
    useEffect(() => {
        if (authLoaded) {
            console.log('Target IDs:', targetIds);
            fetchSearchResults(currentPage);
        }
    }, [authLoaded, currentPage, targetIdRaw]);

    const fetchSearchResults = (page: number) => {
        if (!authLoaded) return;
        
        setLoading(true);
        
        // Construct the URL with appropriate query parameters
        let url = `${apiUrl}/search-results?limit=${limit}&page=${page}`;
        
        // Add target_id parameters (multiple params for array values)
        if (targetIds.length > 0) {
            // Pass each target_id as a separate parameter for proper array handling
            targetIds.forEach(id => {
                url += `&target_id=${encodeURIComponent(id)}`;
            });
            console.log('Requesting URL:', url);
        }
        
        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        axios.get<ApiResponse>(url, {
            headers
        })
            .then(response => {
                console.log('API Response:', response.data);
                if (Array.isArray(response.data.searchResults)) {
                    setSearchResults(response.data.searchResults);
                    console.log('Search results length:', response.data.searchResults.length);
                } else {
                    console.error('Search results is not an array:', response.data.searchResults);
                    setSearchResults([]);
                }
                
                // Set pagination metadata if available
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
                
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching search results:', error);
                setLoading(false);
            });
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    // Format date if it exists
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return new Intl.DateTimeFormat('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (error) {
            console.warn(`Date parsing failed: ${dateString}`, error);
            return dateString;
        }
    };
    
    // Handle row click to show details
    const handleRowClick = (result: SearchResult) => {
        setSelectedResult(result);
    };

    // CSS for truncated text cells
    const truncatedCellStyle = {
        maxWidth: '150px',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };
    
    // Styles for details popup
    const labelStyle = {
        textAlign: 'right' as const,
        paddingRight: '15px',
        fontWeight: 'bold'
    };
    
    const urlValueStyle = {
        textAlign: 'left' as const,
        wordBreak: 'break-word' as const,
        overflowWrap: 'break-word' as const
    };
    
    const detailsGridStyle = {
        display: 'grid',
        gridTemplateColumns: '180px auto',
        columnGap: '15px',
        rowGap: '10px',
        alignItems: 'baseline'
    };
    
    // Show loading while auth is loading
    if (!authLoaded) {
        return <Loader text="Loading authentication..." />;
    }

    return (
        <div className="container">
            <h1>Search Results</h1>
            {loading ? (
                <Loader text="Loading search results..." />
            ) : searchResults.length > 0 ? (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: '45%' }}>URL</th>
                                <th style={{ width: '35%' }}>Query</th>
                                <th style={{ width: '5%' }}>Rank</th>
                                <th style={{ width: '5%' }}>Target Count</th>
                                <th style={{ width: '10%' }}>Insert Timestamp</th>
                                <th style={{ width: '5%' }}>PDF Check</th>
                            </tr>
                        </thead>
                        <tbody>
                            {searchResults.map(result => (
                                <tr 
                                    key={result._id} 
                                    onClick={() => handleRowClick(result)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td style={{...truncatedCellStyle, maxWidth: '25vw'}} title={result.url}>{result.url}</td>
                                    <td style={{...truncatedCellStyle, maxWidth: '50vw'}} title={result.query}>{result.query}</td>
                                    <td style={{textAlign: 'center'}}>{result.index}</td>
                                    <td style={{textAlign: 'center'}}>{formatTargetIdDisplay(result.target_id)}</td>
                                    <td style={{whiteSpace: 'nowrap', textAlign: 'center'}}>{formatDate(result.insert_timestamp)}</td>
                                    <td style={{textAlign: 'center'}}>
                                        {result.checked_on_pdf_timestamp ?
                                            <i className="fas fa-check" style={{ color: 'green' }} title={`checked on: ${formatDate(result.checked_on_pdf_timestamp)}`}></i> :
                                            <i className="fas fa-times" style={{ color: 'red' }} title="not checked yet"></i>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {/* Page Info Bar */}
                    {pagination && (
                        <div className="pagination-container">
                            <PageInfo
                                count={searchResults.length}
                                total={pagination.totalDocuments}
                                label={"results"}
                                showPageInfo={false}
                                extra={parseTargetIds().length > 0 ? `Target: ${parseTargetIds().join(', ')}` : undefined}
                            />
                            
                            {/* Pagination Controls */}
                            <div className="pagination-controls">
                                <button 
                                    onClick={() => handlePageChange(1)} 
                                    disabled={!pagination.hasPreviousPage}
                                    className="pagination-button"
                                >
                                    <i className="fas fa-angle-double-left"></i>
                                </button>
                                
                                <button 
                                    onClick={() => handlePageChange(pagination.currentPage - 1)} 
                                    disabled={!pagination.hasPreviousPage}
                                    className="pagination-button"
                                >
                                    <i className="fas fa-angle-left"></i>
                                </button>
                                
                                <span className="pagination-page-info">Page {pagination.currentPage} of {pagination.totalPages}</span>
                                
                                <button 
                                    onClick={() => handlePageChange(pagination.currentPage + 1)} 
                                disabled={!pagination.hasNextPage}
                                className="pagination-button"
                            >
                                <i className="fas fa-angle-right"></i>
                            </button>
                            
                            <button 
                                onClick={() => handlePageChange(pagination.totalPages)} 
                                disabled={!pagination.hasNextPage}
                                className="pagination-button"
                            >
                                <i className="fas fa-angle-double-right"></i>
                            </button>
                        </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="no-results">No search results available.</div>
            )}
            
            {/* Details Popup */}
            {selectedResult && (
                <div className="popup-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div className="popup-content" style={{
                        backgroundColor: 'white',
                        padding: '0',
                        borderRadius: '5px',
                        width: '700px',
                        maxWidth: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            backgroundColor: '#f0f0f0',
                            padding: '0.4rem 1rem',
                            borderTopLeftRadius: '5px',
                            borderTopRightRadius: '5px',
                            borderBottom: '1px solid #ddd'
                        }}>
                            <button
                                onClick={() => setSelectedResult(null)}
                                style={{ 
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.3rem',
                                    cursor: 'pointer',
                                    lineHeight: '1'
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        
                        <div style={{ padding: '1.5rem 2rem' }}>
                            <div style={{ ...detailsGridStyle, marginBottom: '20px' }}>
                                <div style={labelStyle}>URL:</div>
                                <div style={urlValueStyle}>
                                    <a href={selectedResult.url} target="_blank" rel="noopener noreferrer">{selectedResult.url}</a>
                                </div>
                                
                                <div style={labelStyle}>Rank:</div>
                                <div>{selectedResult.index}</div>
                                
                                <div style={labelStyle}>Query:</div>
                                <div>{selectedResult.query}</div>
                                
                                <div style={labelStyle}>Target IDs:</div>
                                <div>{formatTargetIdDisplay(selectedResult.target_id)}</div>
                                
                                <div style={labelStyle}>Checked PDF Time:</div>
                                <div>{formatDate(selectedResult.checked_on_pdf_timestamp)}</div>
                                
                                <div style={labelStyle}>Processed:</div>
                                <div>{selectedResult.processed === true ? 'Yes' : selectedResult.processed === false ? 'No' : selectedResult.processed}</div>
                                
                                <div style={labelStyle}>Insert Timestamp:</div>
                                <div>{formatDate(selectedResult.insert_timestamp)}</div>
                                
                                <div style={labelStyle}>Update Timestamp:</div>
                                <div>{formatDate(selectedResult.update_timestamp)}</div>
                                
                                <div style={labelStyle}>CUSIP:</div>
                                <div>{selectedResult.cusip || '-'}</div>
                                
                                <div style={labelStyle}>Has ISIN:</div>
                                <div>{selectedResult.has_isin ? 'Yes' : 'No'}</div>
                                
                                <div style={labelStyle}>ISIN:</div>
                                <div>{selectedResult.isin || '-'}</div>
                                
                                <div style={labelStyle}>Validated:</div>
                                <div>{selectedResult.validated ? 'Yes' : 'No'}</div>
                                
                                <div style={labelStyle}>Checking Failed:</div>
                                <div>{selectedResult.checking_failed ? 'Yes' : 'No'}</div>
                            </div>
                            
                            {/* PDF URLs Section */}
                            {selectedResult.pdf_urls && selectedResult.pdf_urls.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ marginBottom: '0.5rem', textAlign: 'left' }}>PDF URLs</h3>
                                    <ul style={{ paddingLeft: '1.5rem' }}>
                                        {selectedResult.pdf_urls.map((url, index) => (
                                            <li key={`pdf-${index}`} style={{ wordBreak: 'break-word', overflowWrap: 'break-word', marginBottom: '0.5rem' }}>
                                                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {/* Prepare PDF URLs Section */}
                            {selectedResult.prepare_pdf_urls && selectedResult.prepare_pdf_urls.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ marginBottom: '0.5rem', textAlign: 'left' }}>Prepared PDF URLs</h3>
                                    <ul style={{ paddingLeft: '1.5rem' }}>
                                        {selectedResult.prepare_pdf_urls.map((url, index) => (
                                            <li key={`prep-pdf-${index}`} style={{ wordBreak: 'break-word', overflowWrap: 'break-word', marginBottom: '0.5rem' }}>
                                                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                                <button 
                                    onClick={() => setSelectedResult(null)}
                                    style={{ 
                                        padding: '0.5rem 1.5rem',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchResultsView; 